/**
 * /pdf-to-excel — a PDF's tables, as a spreadsheet you can correct first.
 *
 * WHAT EVERY COMPETITOR DOES, AND WHY IT LOSES. Upload, wait, download, hope.
 * The extraction is a guess — a PDF stores no tables, only glyphs at
 * coordinates (see tables.js) — and a guess handed over as a finished .xlsx is
 * the worst possible packaging: the mistakes stay invisible until someone sums
 * a column and the total is wrong. Here the recovered grid is SHOWN, every cell
 * is editable, and the file is built afterwards.
 *
 * THREE THINGS THAT WERE WRONG WITH THAT PROMISE UNTIL 2026-08-30.
 *
 *   1. The correction was not addressable. «Объединить столбцы» and
 *      «Разделить столбец» always acted on the LAST two columns
 *      (`mergeCols(cols-2, cols-1)`), because there was no way to say WHICH.
 *      A detector that drew the boundary wrongly in the middle of a nine-column
 *      invoice therefore could not be corrected at all — while the interface
 *      showed two buttons implying it could. Columns and rows now have an
 *      address: a menu on the column head, a number in the row gutter.
 *
 *   2. Everything arrived in Excel as TEXT. Nobody converts a PDF to a
 *      spreadsheet to look at it; they convert it to sum a column. "10 000 ₽"
 *      as a string sums to zero and sorts before "9". Columns now commit to a
 *      type (types.js) and the writer already knew how to emit numbers and
 *      dates — that half was never the missing piece.
 *
 *   3. A scanned page was a dead end. The message was honest — no text layer,
 *      go and OCR it elsewhere — and it still sent away the person who typed
 *      the same words into the same search box as everyone else. The OCR lane
 *      (ocr.js) feeds the SAME detector, so there is no second pipeline to
 *      drift.
 *
 * Reuses `access-export.js` — the .xlsx writer built for /access-repair and
 * verified part-by-part against Microsoft's own engine.
 */
(function () {
    'use strict';

    var CFG = window.__P2X_CFG || {};
    var I18N = CFG.i18n || {};
    var slug = 'pdf-to-excel';

    function $(id) { return document.getElementById(id); }
    function t(k, f) { return I18N[k] || f || k; }
    function toast(m, kind) { if (window.__toast) window.__toast(m, kind || 'info'); }
    function ae(f, p) {
        try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: slug, f: f }, p || {})); }
        catch (e) {}
    }
    var _funnel = {};
    var STAGE_MAP = { file_picked: 'uploading', found: 'editing', download: 'complete' };
    function funnel(stage, p) {
        if (_funnel[stage]) return; _funnel[stage] = 1;
        try { if (window.__aeFunnel) window.__aeFunnel(stage, p || {}); } catch (e) {}
        try { if (window.__stage && STAGE_MAP[stage]) window.__stage(STAGE_MAP[stage], { tool: slug }); } catch (e) {}
    }

    var S = {
        file: null, tables: [], active: 0, busy: false,
        limitTicket: null, delivered: false, edited: false,
        pages: 0, scanned: 0, scannedPages: [],
        doc: null,            // kept open for the page thumbnails
        ocrDone: false,
        menuCol: -1,
    };

    var _pdfjs = null;
    async function loadPdfJs() {
        if (_pdfjs) return _pdfjs;
        var mod = await import(CFG.pdfjsUrl);
        mod.GlobalWorkerOptions.workerSrc = CFG.pdfjsWorkerUrl;
        _pdfjs = mod;
        return mod;
    }
    var _xp = null;
    async function loadExport() {
        if (_xp) return _xp;
        _xp = await import(CFG.exportUrl);
        return _xp;
    }
    var _fflate = null;
    async function loadFflate() {
        if (_fflate) return _fflate;
        await import(new URL(CFG.fflateUrl, location.href).href);
        _fflate = window.fflate;
        return _fflate;
    }

    // ── reading ──────────────────────────────────────────────────────────

    /** A rectangular spans matrix, one entry per cell, defaulting to 1. */
    function blankSpans(rows, cols) {
        return rows.map(function () {
            var a = [];
            for (var i = 0; i < cols; i++) a.push(1);
            return a;
        });
    }

    function adopt(tb, pageNo, index, source, size) {
        tb.page = pageNo;
        tb.source = source || tb.source;
        // A scanned page has no vector rules and the OCR lane hands over no
        // spans; a table from either source must still have the matrix, or
        // every reader of it needs a null check.
        if (!tb.spans || tb.spans.length !== tb.rows.length) {
            tb.spans = blankSpans(tb.rows, tb.cols);
        }
        if (!tb.bounds) tb.bounds = null;
        tb.pageW = size ? size.w : 0;
        tb.pageH = size ? size.h : 0;
        // 'auto' per column: the type is inferred at export time and shown in
        // the head, but the visitor's override wins and must survive a redraw.
        tb.types = [];
        for (var i = 0; i < tb.cols; i++) tb.types.push('auto');
        tb.name = t('sheet_name', 'Page :page table :n')
            .replace(':page', pageNo).replace(':n', index + 1);
        return tb;
    }

    async function readFile(file) {
        var pdfjsLib = await loadPdfJs();
        var bytes = new Uint8Array(await file.arrayBuffer());
        // pdf.js TRANSFERS the array it is handed; the copy is what keeps the
        // original readable if anything downstream needs it.
        var doc = await pdfjsLib.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise;

        var found = [], scanned = 0, scannedPages = [];
        for (var i = 1; i <= doc.numPages; i++) {
            setStatus(t('phase_read', 'Reading page') + ' ' + i + '/' + doc.numPages, i / doc.numPages);
            var page = await doc.getPage(i);
            var R = await window.P2XReader.readPage(pdfjsLib, page, 1);
            if (!R.items.length) { scanned++; scannedPages.push(i); page.cleanup(); continue; }
            var tables = window.P2XTables.extract(R.items, R.lines);
            tables.forEach(function (tb, k) {
                found.push(adopt(tb, i, k, tb.source, { w: R.width, h: R.height }));
            });
            page.cleanup();
        }
        S.pages = doc.numPages;
        S.scanned = scanned;
        S.scannedPages = scannedPages;
        // Held open, not destroyed: the thumbnails and the recognition lane both
        // read pages from it later. The PREVIOUS one has to go, though —
        // dropping the reference without destroying leaks a pdf.js worker per
        // file picked, and picking several in a row is the normal way to use
        // this tool.
        if (S.doc && S.doc !== doc) { try { S.doc.destroy(); } catch (e) {} }
        S.doc = doc;
        return found;
    }

    /**
     * Clamp the spans matrix back to something a spreadsheet can express.
     *
     * Every column operation can invalidate a span — deleting a column shortens
     * the row under a merge, splitting one lengthens it — and an out-of-range
     * or overlapping <mergeCell> is not a cosmetic problem: Excel refuses to
     * open the file at all. So the matrix is normalised after each edit rather
     * than each edit trying to be clever about it.
     */
    function normaliseSpans(tb) {
        if (!tb.spans || tb.spans.length !== tb.rows.length) {
            tb.spans = blankSpans(tb.rows, tb.cols);
            return;
        }
        tb.spans = tb.spans.map(function (row) {
            var out = [], c;
            for (c = 0; c < tb.cols; c++) {
                out.push(Math.max(1, Math.min(Number(row[c]) || 1, tb.cols - c)));
            }
            // A cell covered by its neighbour's span cannot start one of its own.
            for (c = 0; c < tb.cols; c++) {
                if (out[c] > 1) {
                    for (var q = c + 1; q < c + out[c]; q++) out[q] = 1;
                }
            }
            return out;
        });
    }

    // ── the scanned lane ─────────────────────────────────────────────────

    async function offerOcr() {
        var box = $('p2xOcrOffer');
        if (!box || S.ocrDone || !S.scanned) return;
        var txt = $('p2xOcrText');
        if (txt) {
            txt.textContent = S.scanned >= S.pages
                ? t('ocr_text_all', '')
                : t('ocr_text_some', '').replace(':n', S.scanned);
        }
        box.hidden = false;
        await showOcrSize();
        ae('ocr_offered', { scanned: S.scanned, pages: S.pages });
    }

    async function showOcrSize() {
        var el = $('p2xOcrSize'), sel = $('p2xOcrLang');
        if (!el || !window.P2XOcr) return;
        var mb = await window.P2XOcr.sizeMb((sel && sel.value) || 'en');
        if (mb === null) { el.textContent = ''; return; }
        el.textContent = mb === 0
            ? t('ocr_size_ready', 'Model already downloaded')
            : t('ocr_size', ':n MB to download').replace(':n', mb);
    }

    async function runOcr() {
        var btn = $('p2xOcrGo');
        var lang = ($('p2xOcrLang') || {}).value || 'en';
        if (!window.P2XOcr || !window.PPOCR) { toast(t('ocr_failed', 'Recognition failed'), 'error'); return; }
        if (btn) btn.disabled = true;

        // A run that found nothing released its ticket; recognising is the work
        // this file is actually for, so it takes one of its own.
        if (window.__toolLimit && !S.limitTicket) {
            S.limitTicket = await window.__toolLimit.begin(slug, [S.file.name + ':' + S.file.size]);
            if (!S.limitTicket) { if (btn) btn.disabled = false; return; }
        }

        var offer = $('p2xOcrOffer');
        if (offer) offer.hidden = true;
        show('p2xWork');
        setStatus(t('ocr_model', 'Loading the recognition model'), 0.02);
        ae('ocr_start', { lang: lang, scanned: S.scanned });

        try {
            var pdfjsLib = await loadPdfJs();
            var eng = await window.P2XOcr.ensure(lang, {
                workerUrl: CFG.ocrWorkerUrl,
                onModelProgress: function (f) { setStatus(t('ocr_model', ''), 0.02 + f * 0.28); },
            });

            var targets = S.scannedPages.slice();
            var added = 0;
            for (var i = 0; i < targets.length; i++) {
                var no = targets[i];
                setStatus(t('ocr_stage', 'Recognising page') + ' ' + (i + 1) + '/' + targets.length,
                    0.3 + ((i + 1) / targets.length) * 0.68);
                var page = await S.doc.getPage(no);
                var R = await window.P2XOcr.readPage(pdfjsLib, page, eng);
                page.cleanup();
                if (!R.items.length) continue;
                var tables = window.P2XTables.extract(R.items, R.lines);
                for (var k = 0; k < tables.length; k++) {
                    S.tables.push(adopt(tables[k], no, k, 'ocr', { w: R.width, h: R.height }));
                    added++;
                }
            }

            S.ocrDone = true;
            S.scanned = 0;              // those pages are read now
            S.scannedPages = [];
            ae('ocr_done', { added: added });

            if (!S.tables.length) {
                var why = $('p2xEmptyWhy');
                if (why) why.textContent = t('ocr_none', '');
                abortTicket();
                show('p2xEmpty');
                return;
            }
            S.tables.sort(function (a, b) { return a.page - b.page; });
            S.active = 0;
            renderAll();
            show('p2xResult');
        } catch (e) {
            toast(t('ocr_failed', 'Recognition failed'), 'error');
            if (window.__aeDiagnostic) {
                window.__aeDiagnostic({ issue: 'ocr_failed', msg: String((e && e.message) || e).slice(0, 160) });
            }
            abortTicket();
            show(S.tables.length ? 'p2xResult' : 'p2xEmpty');
        } finally {
            if (btn) btn.disabled = false;
        }
    }

    // ── view ─────────────────────────────────────────────────────────────

    function show(id) {
        ['p2xIntake', 'p2xWork', 'p2xResult', 'p2xEmpty'].forEach(function (x) {
            var el = $(x);
            if (el) el.hidden = (x !== id);
        });
        closeMenu();
    }

    function setStatus(label, ratio) {
        var l = $('p2xPhase'); if (l) l.textContent = label;
        var b = $('p2xBar');
        if (b) b.style.width = Math.round(Math.max(0, Math.min(1, ratio || 0)) * 100) + '%';
    }

    function renderAll() {
        renderTabs();
        renderGrid();
        renderSummary();
        renderThumb();
    }

    /**
     * A table continuing on the next page is the commonest real shape and the
     * one nobody handles: the header appears once, the rows keep going. It is
     * only offered when the geometry agrees (same column count, same page), so
     * a "join" never silently welds two unrelated tables together.
     */
    /** How much two x-ranges overlap, as a share of the narrower one. */
    function rangeOverlap(a, b) {
        if (!a || !b) return 0;
        var ov = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
        if (ov <= 0) return 0;
        return ov / Math.max(1, Math.min(a.x1 - a.x0, b.x1 - b.x0));
    }

    function extent(bounds) {
        if (!bounds || !bounds.length) return null;
        return { x0: bounds[0].x0, x1: bounds[bounds.length - 1].x1 };
    }

    /**
     * Is the next table the SAME table, continuing?
     *
     * The first version of this demanded an IDENTICAL column count, and that
     * is the wrong question. A table running over a page break is exactly the
     * case where the detector legitimately finds a different number of
     * boundaries: the continuation page carries no header row, so a column
     * whose only wide entry was its heading collapses, and a narrow column
     * that happens to be empty on page 2 disappears entirely. Demanding
     * equality therefore refused the join precisely when it was most needed —
     * and offered it only for the easy case that needed it least.
     *
     * The honest test is geometric: the two tables occupy the same horizontal
     * band, and most of the narrower one's column boundaries line up with the
     * wider one's. Mismatched counts are then reconciled by joinNext().
     */
    function continuationIndex(i) {
        var a = S.tables[i], b = S.tables[i + 1];
        if (!a || !b) return -1;
        // The LAST page already folded in, not the first: after joining 1+2 the
        // table still calls itself page 1, so asking about `a.page` would refuse
        // page 3 — and a table running over three pages is not exotic.
        var lastPage = (a.pagesJoined && a.pagesJoined[a.pagesJoined.length - 1]) || a.page;
        if (b.page !== lastPage + 1 && b.page !== lastPage) return -1;
        // No geometry (an OCR table): fall back to the old, stricter question.
        if (!a.bounds || !b.bounds) return a.cols === b.cols ? i + 1 : -1;
        if (rangeOverlap(extent(a.bounds), extent(b.bounds)) < 0.7) return -1;

        var wide = a.bounds.length >= b.bounds.length ? a.bounds : b.bounds;
        var narrow = wide === a.bounds ? b.bounds : a.bounds;
        var matched = 0;
        for (var n = 0; n < narrow.length; n++) {
            for (var w = 0; w < wide.length; w++) {
                if (rangeOverlap(narrow[n], wide[w]) >= 0.6) { matched++; break; }
            }
        }
        return matched >= Math.max(2, Math.ceil(narrow.length * 0.7)) ? i + 1 : -1;
    }

    /**
     * Map each column of `from` onto the column of `to` it overlaps most.
     * -1 where nothing overlaps, which the caller treats as "drop nothing,
     * keep the value out of the wrong column".
     */
    function columnMap(from, to) {
        var map = [];
        for (var i = 0; i < from.length; i++) {
            var best = -1, bestOv = 0;
            for (var j = 0; j < to.length; j++) {
                var ov = rangeOverlap(from[i], to[j]);
                if (ov > bestOv) { bestOv = ov; best = j; }
            }
            map.push(best);
        }
        return map;
    }

    function joinNext() {
        var j = continuationIndex(S.active);
        if (j < 0) return;
        var a = S.tables[S.active], b = S.tables[j];
        // The second table's first row is its own header only if it repeats the
        // first one's; otherwise it is data and dropping it would lose a row.
        // U+0001 spelled out, not typed: a literal control byte here is
        // invisible in every editor and ungreppable. It IS the right separator
        // — joining with '' would make ['AB','C'] and ['A','BC'] compare equal
        // — so it stays, as an escape.
        var head = (a.rows[0] || []).join('\u0001');
        var bHead = (b.rows[0] || []).join('\u0001');
        var drop = (head && head === bHead) ? 1 : 0;
        var body = b.rows.slice(drop);
        var bodySpans = (b.spans || blankSpans(b.rows, b.cols)).slice(drop);

        if (b.cols !== a.cols) {
            // Different shapes. Every value goes under the column of A whose
            // x-range it actually sits in. Truncating the extra columns, or
            // padding with empties on the right, would shift a whole page of
            // rows one column across — the silent kind of wrong this tool
            // exists to prevent.
            var map = (a.bounds && b.bounds) ? columnMap(b.bounds, a.bounds) : null;
            body = body.map(function (row) {
                var out = [];
                for (var c = 0; c < a.cols; c++) out.push('');
                row.forEach(function (v, ci) {
                    var target = map ? map[ci] : (ci < a.cols ? ci : -1);
                    if (target == null || target < 0 || target >= a.cols) return;
                    out[target] = out[target] ? (out[target] + ' ' + v).trim() : v;
                });
                return out;
            });
            bodySpans = body.map(function () {
                var r = [];
                for (var c = 0; c < a.cols; c++) r.push(1);
                return r;
            });
            ae('join_reshaped', { from: b.cols, to: a.cols });
        }

        a.rows = a.rows.concat(body);
        a.spans = (a.spans || blankSpans(a.rows, a.cols)).concat(bodySpans);
        a.pagesJoined = (a.pagesJoined || [a.page]).concat([b.page]);
        // A keeps its own geometry: it is the shape everything else is mapped
        // onto, and replacing it with B's would make each join re-interpret the
        // rows already gathered.

        a.name = t('sheet_name_joined', ':from-:to')
            .replace(':from', a.pagesJoined[0])
            .replace(':to', a.pagesJoined[a.pagesJoined.length - 1]);
        S.tables.splice(j, 1);
        if (S.active > j) S.active--;
        S.edited = true;
        ae('join_pages', { rows: body.length, reshaped: b.cols !== a.cols ? 1 : 0 });
        renderAll();
    }

    function renderTabs() {
        var host = $('p2xTabs');
        if (!host) return;
        host.innerHTML = '';
        S.tables.forEach(function (tb, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'p2x-tab' + (i === S.active ? ' on' : '');
            b.textContent = tb.name + ' · ' + tb.rows.length + '×' + tb.cols;
            b.addEventListener('click', function () { S.active = i; renderAll(); });
            host.appendChild(b);
        });
        var j = continuationIndex(S.active);
        if (j >= 0) {
            var jb = document.createElement('button');
            jb.type = 'button';
            jb.className = 'p2x-join';
            jb.textContent = t('join_next', 'Join page :n').replace(':n', S.tables[j].page);
            jb.addEventListener('click', joinNext);
            host.appendChild(jb);
        }
        host.hidden = S.tables.length < 2 && j < 0;
    }

    /** Column type as the head should show it — the inference, or the override. */
    function typeOf(tb, c) {
        var forced = tb.types[c] || 'auto';
        if (forced !== 'auto') return forced;
        if (!window.P2XTypes) return 'text';
        var col = tb.rows.slice(1).map(function (r) { return r[c]; });
        return window.P2XTypes.inferColumn(col).type;
    }

    function renderGrid() {
        var host = $('p2xGrid');
        var tb = S.tables[S.active];
        if (!host || !tb) return;
        host.innerHTML = '';
        var table = document.createElement('table');
        table.className = 'p2x-table';

        var thead = document.createElement('thead');
        var hr = document.createElement('tr');
        var corner = document.createElement('th');
        corner.className = 'p2x-rownum';
        corner.textContent = '';
        hr.appendChild(corner);

        var headSpans = (tb.spans && tb.spans[0]) || [];
        var skipHead = 0;
        (tb.rows[0] || []).forEach(function (cell, ci) {
            if (skipHead > 0) { skipHead--; return; }
            var th = document.createElement('th');
            var hSpan = Math.max(1, Math.min(headSpans[ci] || 1, tb.cols - ci));
            if (hSpan > 1) { th.colSpan = hSpan; skipHead = hSpan - 1; th.classList.add('is-merged'); }
            var wrap = document.createElement('div');
            wrap.className = 'p2x-colhead';

            var name = document.createElement('div');
            name.className = 'p2x-colname';
            name.contentEditable = 'true';
            name.textContent = cell;
            name.setAttribute('aria-label',
                t('a11y_cell', 'Row :r, column :c').replace(':r', 1).replace(':c', ci + 1));
            name.addEventListener('input', function () {
                tb.rows[0][ci] = name.textContent;
                markEdited();
            });

            var meta = document.createElement('div');
            meta.className = 'p2x-colmeta';
            var kind = typeOf(tb, ci);
            var badge = document.createElement('span');
            badge.className = 'p2x-type' + (kind === 'number' ? ' is-num' : (kind === 'date' ? ' is-date' : ''));
            badge.textContent = t('type_' + kind, kind);
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'p2x-colbtn';
            btn.textContent = '⋯';
            btn.setAttribute('aria-haspopup', 'menu');
            btn.setAttribute('aria-label', t('col', 'Column') + ' ' + (ci + 1));
            btn.addEventListener('click', function (ev) {
                ev.stopPropagation();
                openMenu(ci, btn);
            });
            meta.appendChild(badge);
            meta.appendChild(btn);

            wrap.appendChild(name);
            wrap.appendChild(meta);
            th.appendChild(wrap);
            hr.appendChild(th);
        });
        thead.appendChild(hr);
        table.appendChild(thead);

        var tbody = document.createElement('tbody');
        tb.rows.slice(1).forEach(function (row, ri) {
            var tr = document.createElement('tr');
            var num = document.createElement('td');
            num.className = 'p2x-rownum';
            num.textContent = String(ri + 1);
            num.title = t('row_delete', 'Delete row');
            num.addEventListener('click', function () {
                tb.rows.splice(ri + 1, 1);
                if (tb.spans) tb.spans.splice(ri + 1, 1);
                S.edited = true;
                ae('delete_row', {});
                renderAll();
            });
            tr.appendChild(num);

            var rowSpans = (tb.spans && tb.spans[ri + 1]) || [];
            var skip = 0;
            row.forEach(function (cell, ci) {
                if (skip > 0) { skip--; return; }
                var td = document.createElement('td');
                var span = Math.max(1, Math.min(rowSpans[ci] || 1, tb.cols - ci));
                if (span > 1) { td.colSpan = span; skip = span - 1; td.classList.add('is-merged'); }
                td.contentEditable = 'true';
                td.textContent = cell;
                td.setAttribute('aria-label',
                    t('a11y_cell', 'Row :r, column :c').replace(':r', ri + 2).replace(':c', ci + 1));
                td.addEventListener('input', function () {
                    tb.rows[ri + 1][ci] = td.textContent;
                    markEdited();
                });
                tr.appendChild(td);
            });
            tbody.appendChild(tr);
        });
        table.appendChild(tbody);
        host.appendChild(table);

        var note = $('p2xSource');
        if (note) {
            note.textContent = tb.source === 'rules' ? t('src_rules', '')
                : (tb.source === 'ocr' ? t('src_ocr', '') : t('src_gaps', ''));
        }
    }

    function markEdited() {
        if (!S.edited) { S.edited = true; ae('edit_cell', {}); }
    }

    function renderSummary() {
        var el = $('p2xSummary');
        if (!el) return;
        var cells = S.tables.reduce(function (n, tb) { return n + tb.rows.length * tb.cols; }, 0);
        el.textContent = [
            t('sum_tables', 'Tables: :n').replace(':n', S.tables.length),
            t('sum_cells', 'Cells: :n').replace(':n', cells),
            t('sum_pages', 'Pages: :n').replace(':n', S.pages),
        ].join(' · ');

        var warn = $('p2xScanned');
        if (warn) {
            warn.hidden = !S.scanned;
            if (S.scanned) {
                warn.textContent = t('note_scanned', '').replace(':n', S.scanned);
            }
        }
    }

    /**
     * The page the active table came from, with its region outlined.
     *
     * `box` was computed by the detector from the first day and used for
     * nothing. Showing it is what makes the grid checkable: a person who cannot
     * see WHERE a table was taken from has to read every cell to trust it.
     */
    var thumbToken = 0;
    async function renderThumb() {
        var cv = $('p2xThumb');
        var tb = S.tables[S.active];
        if (!cv || !tb || !S.doc) return;
        var token = ++thumbToken;
        var page;
        try { page = await S.doc.getPage(tb.page); } catch (e) { return; }
        var base = page.getViewport({ scale: 1 });
        var w = Math.max(120, cv.clientWidth || 200);
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var k = (w * dpr) / base.width;
        var vp = page.getViewport({ scale: k });
        cv.width = Math.round(vp.width);
        cv.height = Math.round(vp.height);
        cv.style.height = Math.round(vp.height / dpr) + 'px';
        var g = cv.getContext('2d');
        g.fillStyle = '#fff';
        g.fillRect(0, 0, cv.width, cv.height);
        try {
            await page.render({ canvasContext: g, viewport: vp }).promise;
        } catch (e) { /* a page that will not draw still gets its outline */ }
        page.cleanup();
        if (token !== thumbToken) return;

        if (tb.box) {
            // The detector works in a scale-1 viewport, which is exactly `base`,
            // so the only conversion is the thumbnail's own scale.
            g.save();
            g.strokeStyle = 'rgba(37,99,235,.95)';
            g.lineWidth = Math.max(1.5, 2 * dpr);
            g.fillStyle = 'rgba(37,99,235,.14)';
            var x = tb.box.x * k, y = tb.box.y * k, bw = tb.box.w * k, bh = tb.box.h * k;
            g.fillRect(x, y, bw, bh);
            g.strokeRect(x, y, bw, bh);
            g.restore();
        }
    }

    // ── column surgery ───────────────────────────────────────────────────

    /**
     * The detector draws one boundary per corridor. Where it drew one too many
     * or one too few, moving the boundary inside the PDF is not something a
     * visitor can do — but merging two columns of the RESULT, splitting one at
     * its separator, or dropping one outright, is.
     *
     * Every operation takes an INDEX. The previous version had the same three
     * ideas with the index hard-wired to the last column, which made them
     * decoration on any table whose mistake was in the middle.
     */
    function mergeRight(a) {
        var tb = S.tables[S.active];
        if (!tb || a < 0 || a + 1 >= tb.cols) return;
        tb.rows = tb.rows.map(function (row) {
            var out = row.slice();
            out[a] = [row[a], row[a + 1]].filter(Boolean).join(' ').trim();
            out.splice(a + 1, 1);
            return out;
        });
        tb.types.splice(a + 1, 1);
        tb.types[a] = 'auto';
        tb.spans = (tb.spans || []).map(function (r) { var o = r.slice(); o.splice(a + 1, 1); o[a] = 1; return o; });
        tb.cols--;
        normaliseSpans(tb);
        S.edited = true;
        ae('merge_cols', { at: a });
        renderAll();
    }

    function splitCol(idx) {
        var tb = S.tables[S.active];
        if (!tb || idx < 0 || idx >= tb.cols) return;
        tb.rows = tb.rows.map(function (row) {
            var v = String(row[idx] || '');
            // Split at the widest run of spaces — the same signal the detector
            // uses, applied to one cell instead of a page.
            var m = v.match(/^(.*?)(\s{2,}|\s(?=\S*\d))(.*)$/);
            var left = m ? m[1].trim() : v;
            var right = m ? m[3].trim() : '';
            var out = row.slice();
            out.splice(idx, 1, left, right);
            return out;
        });
        tb.types.splice(idx, 1, 'auto', 'auto');
        tb.spans = (tb.spans || []).map(function (r) { var o = r.slice(); o.splice(idx, 1, 1, 1); return o; });
        tb.cols++;
        normaliseSpans(tb);
        S.edited = true;
        ae('split_col', { at: idx });
        renderAll();
    }

    function deleteCol(idx) {
        var tb = S.tables[S.active];
        if (!tb || tb.cols <= 1) return;
        tb.rows = tb.rows.map(function (row) {
            var out = row.slice();
            out.splice(idx, 1);
            return out;
        });
        tb.types.splice(idx, 1);
        tb.spans = (tb.spans || []).map(function (r) { var o = r.slice(); o.splice(idx, 1); return o; });
        tb.cols--;
        normaliseSpans(tb);
        S.edited = true;
        ae('delete_col', { at: idx });
        renderAll();
    }

    function setType(idx, kind) {
        var tb = S.tables[S.active];
        if (!tb) return;
        tb.types[idx] = kind;
        S.edited = true;
        ae('set_type', { at: idx, kind: kind });
        renderGrid();
    }

    // ── column menu ──────────────────────────────────────────────────────

    function openMenu(col, anchor) {
        var menu = $('p2xMenu');
        var tb = S.tables[S.active];
        if (!menu || !tb) return;
        S.menuCol = col;

        menu.querySelector('[data-act="merge"]').disabled = col + 1 >= tb.cols;
        menu.querySelector('[data-act="delete"]').disabled = tb.cols <= 1;
        var current = tb.types[col] || 'auto';
        menu.querySelectorAll('[data-type]').forEach(function (b) {
            b.classList.toggle('is-on', b.dataset.type === current);
        });

        menu.hidden = false;
        var r = anchor.getBoundingClientRect();
        var mw = menu.offsetWidth || 190, mh = menu.offsetHeight || 220;
        var left = Math.min(r.left, window.innerWidth - mw - 8);
        var top = r.bottom + 4;
        if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
        menu.style.left = Math.max(8, left) + 'px';
        menu.style.top = top + 'px';
    }

    function closeMenu() {
        var menu = $('p2xMenu');
        if (menu) menu.hidden = true;
        S.menuCol = -1;
    }

    // ── export ───────────────────────────────────────────────────────────

    function outName(ext) {
        var base = (S.file && S.file.name) || 'tables';
        var dot = base.lastIndexOf('.');
        var stem = dot > 0 ? base.slice(0, dot) : base;
        if (window.__outputName) {
            return window.__outputName.join([window.__outputName.sanitize(stem)], ext);
        }
        return stem + '.' + ext;
    }

    /**
     * Head labels, typed body and the merge ranges, for one table.
     *
     * `merges` is indexed over [header, ...rows] — r:0 is the header — which is
     * exactly what the writer expects, and exactly the indexing `tb.spans`
     * already uses. A spanned cell's value sits in its FIRST column and the
     * rest are empty: Excel's own model for a merged cell, and the reason a
     * heading over two columns no longer lands in whichever one its midpoint
     * happened to fall into.
     */
    function shape(tb) {
        var head = tb.rows[0] || [];
        var names = head.map(function (h, i) { return h || (t('col', 'Column') + ' ' + (i + 1)); });
        var typed = window.P2XTypes
            ? window.P2XTypes.applyTypes(tb.rows, tb.types).rows
            : tb.rows.slice(1);
        var merges = [];
        (tb.spans || []).forEach(function (row, r) {
            (row || []).forEach(function (span, c) {
                if (span > 1 && c + span <= tb.cols) merges.push({ r: r, c: c, span: span });
            });
        });
        return { name: tb.name, colNames: names, rows: typed, merges: merges };
    }

    async function exportXlsx() {
        var xp = await loadExport();
        var fflate = await loadFflate();
        var bytes = xp.toXlsx(S.tables.map(shape), fflate.zipSync);
        return new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
    }

    async function exportCsv(table) {
        var xp = await loadExport();
        var s = shape(table || S.tables[S.active]);
        // CSV has no cell types, so a Date must be written as something a
        // spreadsheet will re-read; ISO is the only form every locale agrees on.
        var rows = s.rows.map(function (r) {
            return r.map(function (v) {
                return (v && typeof v === 'object' && typeof v.toISOString === 'function')
                    ? v.toISOString().slice(0, 10) : v;
            });
        });
        var csv = xp.toCsv(s.colNames, rows);
        // BOM: Excel opens a UTF-8 CSV as cp1251 without it, and every Cyrillic
        // cell arrives as mojibake — the single most common complaint about
        // "CSV export" anywhere.
        return new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    }

    /** A file name for one table's CSV, unique inside the batch. */
    function csvName(tb, used) {
        var base = (S.file && S.file.name) || 'tables';
        var dot = base.lastIndexOf('.');
        var stem = dot > 0 ? base.slice(0, dot) : base;
        var raw = stem + ' - ' + (tb.name || 'table');
        var name;
        if (window.__outputName) {
            name = window.__outputName.join([window.__outputName.sanitize(raw)], 'csv');
            // distinct() READS the used set, it does not add to it. Two tables
            // whose names collide would otherwise be written to the same file
            // and the second would silently replace the first.
            if (window.__outputName.distinct) name = window.__outputName.distinct(name, used);
        } else {
            name = raw.replace(/[\\/:*?"<>|]+/g, '_') + '.csv';
        }
        used[name] = 1;
        return name;
    }

    /**
     * CSV used to hand over ONLY the open table, with the fact tucked into the
     * button label. XLSX has always taken the whole workbook, so the same press
     * meant two different amounts of work depending on which button it was —
     * and the person who came for "the tables in this PDF" got one of them.
     *
     * Now CSV covers all of them: one file per table, through the shared batch
     * lane (folder -> one archive -> honest refusal). A single table still
     * downloads as a single file, because wrapping one CSV in a ZIP is a chore,
     * not a feature.
     */
    async function downloadCsvAll() {
        // FIRST await in the click: showDirectoryPicker needs the activation
        // this handler is still holding, and any earlier await can spend it.
        var many = S.tables.length > 1;
        var batch = null;
        if (many && window.__batchDeliver) {
            batch = await window.__batchDeliver.openBatch({
                slug: slug,
                zipName: outName('zip'),
                total: S.tables.length,
            });
        }
        if (!batch) {
            var one = await exportCsv(S.tables[S.active]);
            if (window.__downloadFile) window.__downloadFile(one, outName('csv'));
            return { via: 'single', written: 1 };
        }
        var used = Object.create(null);
        for (var i = 0; i < S.tables.length; i++) {
            var blob = await exportCsv(S.tables[i]);
            await batch.write(csvName(S.tables[i], used), blob);
        }
        return batch.finish();
    }

    async function download(kind) {
        var btn = kind === 'csv' ? $('p2xCsv') : $('p2xXlsx');
        if (btn) btn.disabled = true;
        try {
            if (kind === 'csv') {
                var res = await downloadCsvAll();
                if (res && res.via === 'refused') { if (btn) btn.disabled = false; return; }
            } else {
                var blob = await exportXlsx();
                if (window.__downloadFile) window.__downloadFile(blob, outName('xlsx'));
            }
            S.delivered = true;
        } catch (e) {
            toast(t('err_export', 'Could not build the file'), 'error');
            if (window.__aeDiagnostic) {
                window.__aeDiagnostic({ issue: 'export_failed', msg: String((e && e.message) || e).slice(0, 160) });
            }
            if (btn) btn.disabled = false;
            return;
        }
        try {
            if (S.limitTicket) { S.limitTicket.commit(); S.limitTicket = null; }
            funnel('download', {
                kind: kind, tables: S.tables.length, edited: S.edited ? 1 : 0,
                sources: S.tables.map(function (x) { return x.source; }).join(','),
                typed: S.tables.reduce(function (n, tb) {
                    for (var c = 0; c < tb.cols; c++) if (typeOf(tb, c) !== 'text') n++;
                    return n;
                }, 0),
            });
            if (window.__aeToolComplete) window.__aeToolComplete({ tables: S.tables.length });
        } catch (e) { /* доставленный файл не отменяется бухгалтерией */ }
        if (btn) btn.disabled = false;
    }

    function abortTicket() {
        if (S.limitTicket) {
            try { S.limitTicket.abort(); } catch (e) {}
            S.limitTicket = null;
        }
    }

    // ── intake ───────────────────────────────────────────────────────────

    async function accept(file) {
        if (S.busy) return;
        S.busy = true;
        S.file = file; S.tables = []; S.active = 0; S.edited = false;
        S.delivered = false; S.ocrDone = false;
        S.scanned = 0; S.scannedPages = [];
        // The offer belongs to the file that raised it: a second document with a
        // text layer everywhere would otherwise still show "these pages are
        // pictures" from the first one.
        var prevOffer = $('p2xOcrOffer');
        if (prevOffer) prevOffer.hidden = true;

        if (window.__toolLimit) {
            S.limitTicket = await window.__toolLimit.begin(slug, [file.name + ':' + file.size]);
            if (!S.limitTicket) { S.busy = false; return; }
        }

        funnel('file_picked', { bytes: file.size });
        show('p2xWork');
        setStatus(t('phase_read', 'Reading the document'), 0.02);

        try {
            S.tables = await readFile(file);
            if (!S.tables.length) {
                // Not an error, and not silence either: the visitor needs to
                // know WHY, because "no tables" and "no text at all" call for
                // completely different next steps — and one of them is now a
                // button on this page rather than a different tool.
                var el = $('p2xEmptyWhy');
                if (el) {
                    el.textContent = S.scanned >= S.pages
                        ? t('empty_scanned', '')
                        : t('empty_no_tables', '');
                }
                funnel('found', { tables: 0, scanned: S.scanned });
                ae('no_tables', { pages: S.pages, scanned: S.scanned });
                abortTicket();
                show('p2xEmpty');
                await offerOcr();
                return;
            }
            funnel('found', {
                tables: S.tables.length, pages: S.pages, scanned: S.scanned,
                rules: S.tables.filter(function (x) { return x.source === 'rules'; }).length,
            });
            renderAll();
            show('p2xResult');
            await offerOcr();
        } catch (e) {
            abortTicket();
            toast(t('err_read', 'Could not read the PDF'), 'error');
            if (window.__aeDiagnostic) {
                window.__aeDiagnostic({ issue: 'read_failed', msg: String((e && e.message) || e).slice(0, 160) });
            }
            show('p2xIntake');
        } finally {
            S.busy = false;
        }
    }

    function reset() {
        S.tables = []; S.active = 0; S.edited = false;
        S.scanned = 0; S.scannedPages = []; S.ocrDone = false;
        abortTicket();
        if (S.doc) { try { S.doc.destroy(); } catch (e) {} S.doc = null; }
        var offer = $('p2xOcrOffer');
        if (offer) offer.hidden = true;
        show('p2xIntake');
    }

    function init() {
        var up = $('p2xUpload');
        if (up) {
            // The primitive calls back with (File, detail) — not with an event.
            var onFile = function (file, detail) {
                var d = detail || {};
                var f = (d.files && d.files[0]) || file;
                if (f) accept(f);
            };
            if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(up, onFile);
            else {
                up.addEventListener('tool-uploader:file', function (ev) {
                    var d = (ev && ev.detail) || {};
                    onFile(d.file || (d.files && d.files[0]) || null, d);
                });
            }
            up.addEventListener('tool-uploader:clear', reset);
        }

        var x = $('p2xXlsx'); if (x) x.addEventListener('click', function () { download('xlsx'); });
        var c = $('p2xCsv'); if (c) c.addEventListener('click', function () { download('csv'); });
        ['p2xAgain', 'p2xAgain2'].forEach(function (id) {
            var b = $(id);
            if (b) b.addEventListener('click', reset);
        });

        var menu = $('p2xMenu');
        if (menu) {
            menu.addEventListener('click', function (ev) {
                var b = ev.target.closest('button');
                if (!b || b.disabled) return;
                var col = S.menuCol;
                closeMenu();
                if (col < 0) return;
                if (b.dataset.act === 'merge') mergeRight(col);
                else if (b.dataset.act === 'split') splitCol(col);
                else if (b.dataset.act === 'delete') deleteCol(col);
                else if (b.dataset.type) setType(col, b.dataset.type);
            });
        }
        document.addEventListener('click', function (ev) {
            var menuEl = $('p2xMenu');
            if (menuEl && !menuEl.hidden && !menuEl.contains(ev.target)) closeMenu();
        });
        document.addEventListener('keydown', function (ev) {
            if (ev.key === 'Escape') closeMenu();
        });
        window.addEventListener('resize', closeMenu);

        var ocrGo = $('p2xOcrGo');
        if (ocrGo) ocrGo.addEventListener('click', runOcr);
        var ocrLang = $('p2xOcrLang');
        if (ocrLang) ocrLang.addEventListener('change', showOcrSize);

        window.addEventListener('pagehide', function () {
            if (S.tables.length && !S.delivered) ae('left_without_download', { tables: S.tables.length });
        });

        // Read-only introspection for the browser tests (the house __xxDebug
        // convention). A grid rendered into contenteditable cells has no other
        // handle a test can hold.
        window.__p2xDebug = {
            get tables() {
                return S.tables.map(function (tb) {
                    return { cols: tb.cols, rows: tb.rows, source: tb.source, page: tb.page,
                        name: tb.name, pagesJoined: tb.pagesJoined,
                        types: tb.types, spans: tb.spans, bounds: tb.bounds };
                });
            },
            get active() { return S.active; },
            get pages() { return S.pages; },
            get scanned() { return S.scanned; },
            get typesOf() {
                var tb = S.tables[S.active];
                if (!tb) return [];
                var out = [];
                for (var i = 0; i < tb.cols; i++) out.push(typeOf(tb, i));
                return out;
            },
            shape: function () { return S.tables[S.active] ? shape(S.tables[S.active]) : null; },
        };
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
